import React, { memo } from 'react';
import { cn } from '@/lib/utils';
import { 
  X, 
  Eye, 
  EyeOff, 
  Archive, 
  Trash2, 
  Tag, 
  UserPlus,
  MoreHorizontal,
  Download,
  Undo2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// ============================================================================
// BULK ACTION TOOLBAR
// ============================================================================

export const BulkActionToolbar = memo(({
  selectedCount = 0,
  hasUnreadSelected = false,
  onCancel,
  onMarkRead,
  onMarkUnread,
  onArchive,
  onDelete,
  onAssign,
  onAddTag,
  onExport,
  isLoading = false,
  className = ''
}) => {
  if (selectedCount === 0) return null;

  return (
    <div 
      className={cn(
        'sticky top-0 z-20 animate-in slide-in-from-top-2 duration-200',
        'bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800',
        'rounded-xl shadow-lg border border-blue-500/20',
        'px-4 py-2.5',
        className
      )}
    >
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        {/* Left: Selection Info */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button
            onClick={onCancel}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            title="Batalkan pilihan"
            data-testid="bulk-cancel-btn"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-white font-semibold text-sm">
              {selectedCount}
            </span>
            <span className="text-blue-100 text-xs sm:text-sm truncate">dipilih</span>
          </div>
        </div>

        {/* Right: Action Buttons */}
        <div className="flex items-center gap-1">
          {/* Satu aksi baca/belum-baca yang kontekstual: kalau ada pilihan yang
              belum dibaca -> "Tandai dibaca", kalau semuanya sudah dibaca ->
              "Tandai belum dibaca". Dua tombol sekaligus membingungkan user. */}
          <Button
            size="sm"
            variant="ghost"
            onClick={hasUnreadSelected ? onMarkRead : onMarkUnread}
            disabled={isLoading}
            className="text-white hover:bg-white/20 h-8 px-2 sm:px-3"
            title={hasUnreadSelected ? 'Tandai sudah dibaca' : 'Tandai belum dibaca'}
            data-testid="bulk-toggle-read-btn"
          >
            {hasUnreadSelected ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            <span className="ml-1.5 text-xs whitespace-nowrap sm:hidden">
              {hasUnreadSelected ? 'Dibaca' : 'Belum dibaca'}
            </span>
            <span className="ml-1.5 text-xs whitespace-nowrap hidden sm:inline">
              {hasUnreadSelected ? 'Tandai dibaca' : 'Tandai belum dibaca'}
            </span>
          </Button>

          {/* Archive */}
          <Button
            size="sm"
            variant="ghost"
            onClick={onArchive}
            disabled={isLoading}
            className="text-white hover:bg-white/20 h-8 px-2 sm:px-3"
            title="Arsipkan"
            data-testid="bulk-archive-btn"
          >
            <Archive className="w-4 h-4" />
            <span className="hidden sm:inline ml-1.5 text-xs">Arsip</span>
          </Button>

          {/* More Actions Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/20 h-8 px-2"
                data-testid="bulk-more-btn"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {onAssign && (
                <DropdownMenuItem onClick={onAssign} className="cursor-pointer">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Assign Agent
                </DropdownMenuItem>
              )}
              
              {onAddTag && (
                <DropdownMenuItem onClick={onAddTag} className="cursor-pointer">
                  <Tag className="w-4 h-4 mr-2" />
                  Add Tag
                </DropdownMenuItem>
              )}
              
              {onExport && (
                <DropdownMenuItem onClick={onExport} className="cursor-pointer">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </DropdownMenuItem>
              )}
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem 
                onClick={onDelete}
                className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:text-red-400 dark:bg-red-950/40"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Hapus
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="absolute inset-0 bg-indigo-600/50 rounded-xl flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
});

BulkActionToolbar.displayName = 'BulkActionToolbar';

export default BulkActionToolbar;
